import React, { useEffect, useRef } from 'react';
import {
  FlatList,
  Image,
  Platform,
  StyleProp,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

import useCssScrollSnap from '../../hooks/use-css-scroll-snap';
import generateID from '../../utils/generate-id';

type Item = {
  id: string;
  imageUri: string;
  title: string;
};

type CarouselItemProp = Item & {
  accessibilityLabel?: string;
  onPress?: (item: CarouselItemProp) => void;
  wrapItem?: boolean;
  /**
   * Automatically generated based on Carousel ID prop
   */
  itemId?: string;
};

type Props = {
  items: Item[];
  onItemPress?: CarouselItemProp['onPress'];
  /**
   * Disables scrolling and forces items to wrap around
   */
  wrapContent?: boolean;
  /**
   * Number of items per row.
   * This disables horizontal scrolling.
   */
  numColumns?: number;
  /**
   * Customize the Carousel container's appearance. Useful for some responsive layout.
   */
  style?: StyleProp<ViewStyle>;
  /**
   * Customize the scrolling viewport's styles
   */
  viewPortStyle?: StyleProp<ViewStyle>;
  /**
   * Accessibility label for Carousel
   */
  accessibilityLabel?: string;
  /**
   * Accessibility label for Carousel Item
   */
  itemAccessibilityLabel?: CarouselItemProp['accessibilityLabel'];
  id?: string;
};

const Carousel = (props: Props) => {
  useCssScrollSnap();
  const flatListRef = useRef<FlatList>(null);

  const numColumns = props.numColumns || 0;

  /**
   * Only for Web
   * Sets custom CSS rules to enable scroll snapping
   */
  useEffect(() => {
    if (Platform.OS === 'web') {
      flatListRef.current?.setNativeProps({
        className: 'rnw-scroll-snap-container',
      });
    }
  }, []);

  return (
    <FlatList
      ref={flatListRef}
      accessibilityLabel={props.accessibilityLabel}
      numColumns={numColumns || undefined}
      key={numColumns}
      data={props.items}
      horizontal={!numColumns}
      snapToAlignment="center"
      decelerationRate="fast"
      snapToInterval={style.item.width - style.itemWrapper.marginRight * 2}
      renderItem={({ item, index }) => (
        <CarouselItem
          {...item}
          onPress={props.onItemPress}
          wrapItem={numColumns > 0}
          key={index}
          accessibilityLabel={
            props.accessibilityLabel &&
            `${props.itemAccessibilityLabel}-${index}`
          }
          /** Generate HTML and Test IDs  */
          itemId={props.id && `${props.id}-item-${index}`}
          isLastItem={index === props.items.length - 1}
        />
      )}
      style={props.style}
      contentContainerStyle={props.viewPortStyle}
      /** Generate HTML and Test IDs  */
      {...generateID(props.id)}
    />
  );
};

const CarouselItem = (props: CarouselItemProp & { isLastItem: boolean }) => {
  const itemRef = useRef<View>(null);

  useEffect(() => {
    if (Platform.OS === 'web') {
      itemRef.current?.setNativeProps({
        className: 'rnw-scroll-snap-item',
      });
    }
  }, []);

  const { id, imageUri, title } = props;

  return (
    <View
      style={[
        style.itemWrapper,
        props.wrapItem && style.itemWrapContent,
        props.isLastItem && style.itemLastWrapper,
      ]}
      ref={itemRef}
    >
      <TouchableOpacity
        accessibilityLabel={props.accessibilityLabel}
        style={style.item}
        onPress={() => props.onPress?.({ id, imageUri, title })}
        /** Generate HTML and Test IDs  */
        {...generateID(props.itemId)}
        /** Platform-specific props */
        {...Platform.select({
          web: {
            accessibilityRole: 'button',
          },
        })}
      >
        <Image
          source={{
            uri: props.imageUri,
          }}
          style={style.itemImage}
        />
        <View style={style.itemTitleWrapper}>
          <Text style={style.itemTitle}>{props.title}</Text>
        </View>
      </TouchableOpacity>
    </View>
  );
};

const style = StyleSheet.create({
  itemWrapper: {
    marginRight: 16,
  },
  itemLastWrapper: {
    marginRight: 0,
  },
  item: {
    width: 272,
    height: 272,
    backgroundColor: 'white',
    borderRadius: 24,
  },
  itemWrapContent: {
    marginVertical: 8,
  },
  itemImage: {
    width: 272,
    height: 200,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
  },
  itemTitleWrapper: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  itemTitle: {
    color: '#E5258C',
    fontWeight: 'bold',
  },
});

export default Carousel;
export { Item };
